import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { Button, Input, Select, Stack, Modal, useModals, ModalProps } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useProbationPolicySave } from "@/queries/use-probation-policies.query.ts";
import {
  ApiProbationPolicy,
  ApiProbationPolicyPayload,
  getProbationPolicyUnitTypeItems,
  ProbationPolicyUnitTypes,
} from "@/types/probation-policy.types.ts";

export interface ProbationPolicyEditModelProps extends ModalProps {
  probationPolicy?: ApiProbationPolicy;
}

export function ProbationPolicyEditModal({
  probationPolicy,
  ...props
}: ProbationPolicyEditModelProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };

  const requiredMessage = t("errors:field_required");
  const ProbationPolicySchemaObject = z.object({
    title: z.string().min(1, requiredMessage),
    amount: z.number({ error: requiredMessage }),
    unit: z.enum(ProbationPolicyUnitTypes),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ApiProbationPolicyPayload>({
    mode: "onSubmit",
    resolver: zodResolver(ProbationPolicySchemaObject),
    defaultValues: {
      title: probationPolicy?.title ?? "",
      amount: probationPolicy?.amount,
      unit: probationPolicy?.unit ?? ProbationPolicyUnitTypes.days,
    },
  });

  const probationPolicySave = useProbationPolicySave(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await probationPolicySave.mutateAsync(
        {
          id: probationPolicy?.id,
          payload: {
            title: data.title,
            amount: data.amount,
            unit: data.unit,
          },
        },
        {
          onSuccess: (data) => {
            if (probationPolicy) {
              analyticsService.trackEvent(analyticEvents.probationPolicies.edited, {
                [analyticProperties.id]: probationPolicy.id,
              });
            } else {
              analyticsService.trackEvent(analyticEvents.probationPolicies.created, {
                [analyticProperties.id]: data.id,
              });
            }

            close(id);
          },
        }
      );
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={t("core:probation_policy")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button
            type={"submit"}
            size={"lg"}
            isLoading={probationPolicySave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("common:title")}
          required={true}
          type={"text"}
          autoFocus={true}
          {...register("title")}
          error={errors.title?.message}
        />

        <Stack gap={"md"} className="grid grid-cols-2 pt-4">
          <Controller
            control={control}
            render={({ field: { value, onChange } }) => (
              <Select
                value={value}
                required={true}
                options={getProbationPolicyUnitTypeItems()}
                onChange={onChange}
                label={t("common:unit_of_time")}
                emptyTitle={""}
              />
            )}
            name={"unit"}
          />
          <Input
            label={t("common:amount")}
            required={true}
            type={"number"}
            {...register("amount", {
              setValueAs: (value) => (value === "" ? undefined : Number(value)),
            })}
            error={errors.amount?.message}
          />
        </Stack>
      </form>
    </Modal>
  );
}
